import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  ArrowRight, ShieldCheck, Clock, Car, Headphones,
  Quote, Star, CalendarCheck, PenLine, Search
} from 'lucide-react';

const CRM_URL = process.env.REACT_APP_CRM_URL || 'https://crm.phillyrideshare.com';
const FLEET_API = `${CRM_URL}/api/public/fleet`;

const HERO_IMG = 'https://images.unsplash.com/photo-1600104355129-604d76f03876?auto=format&fit=crop&w=1600&q=80';
const TESTIMONIAL_IMG = 'https://images.unsplash.com/photo-1758525747638-25563afc9ff5?auto=format&fit=crop&w=1200&q=80';
const CAR_FALLBACKS = [
  'https://images.unsplash.com/photo-1550355291-bbee04a92027?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=800&q=80',
];

const trustPills = [
  { icon: ShieldCheck, label: 'Insurance-ready' },
  { icon: Clock, label: 'Same-day pickup' },
  { icon: Car, label: 'Rideshare approved' },
  { icon: Headphones, label: 'Live human support' },
];

const steps = [
  { n: '01', icon: Search, title: 'Browse the fleet', text: 'Pick a car that fits your schedule and your hustle. Weekly rates, deposit returnable on time return.' },
  { n: '02', icon: CalendarCheck, title: 'Book online', text: 'Choose your pickup window and submit your info. Confirmation in minutes — no phone tag.' },
  { n: '03', icon: PenLine, title: 'Sign & drive', text: 'Bring your license and insurance, pay the deposit via Cash App, and drive off. We handle the rest.' },
];

const LandingPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchData = async () => {
      try {
        const response = await axios.get(FLEET_API);
        setVehicles(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching fleet from CRM:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-24 px-4 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-[0.07]" />
          <div className="absolute -top-32 -right-40 w-[34rem] h-[34rem] bg-amber-200/40 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-4 py-1.5 mb-8 animate-fade-up">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            <span className="text-xs font-medium text-slate-600">Philly's #1 rideshare rental · Trusted by 50+ drivers</span>
          </div>

          <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl text-slate-950 max-w-4xl leading-[1.02] tracking-tight animate-fade-up animate-delay-100">
            Drive more. <span className="text-amber-500">Earn more.</span> Skip the dealer.
          </h1>

          <p className="mt-7 text-lg text-slate-500 max-w-xl leading-relaxed animate-fade-up animate-delay-200">
            Weekly car rentals built for Philadelphia rideshare drivers and everyday commuters. Insurance-ready, mileage-friendly, no long contracts.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4 animate-fade-up animate-delay-300">
            <a href={`${CRM_URL}/book`}>
              <button className="btn-amber text-base px-7 py-3.5" data-testid="hero-browse-btn">
                Browse available cars <ArrowRight className="w-5 h-5" />
              </button>
            </a>
            <a href="#how">
              <button className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-800 hover:border-slate-400 rounded-lg px-7 py-3.5 font-semibold transition-all text-base" data-testid="hero-how-btn">
                How it works
              </button>
            </a>
          </div>

          <div className="mt-14 flex items-center gap-10 animate-fade-up animate-delay-400">
            <div>
              <span className="block font-display text-4xl font-bold text-slate-950">$275</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Per week</span>
            </div>
            <div className="w-px h-12 bg-slate-200"></div>
            <div>
              <span className="block font-display text-4xl font-bold text-slate-950">$200</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Deposit</span>
            </div>
            <div className="w-px h-12 bg-slate-200"></div>
            <div>
              <span className="block font-display text-4xl font-bold text-slate-950">24h</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Approval</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-100 bg-slate-50/60" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {trustPills.map((p, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`trust-pill-${i}`}>
              <p.icon className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-semibold text-slate-700">{p.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Fleet */}
      <section className="py-24 px-4" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <span className="eyebrow">The Fleet</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-950 mt-3">Pick your ride</h2>
            </div>
            <a href={`${CRM_URL}/book`} className="link-amber" data-testid="see-all-fleet-btn">
              See all <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-96 bg-slate-100 rounded-2xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle, idx) => (
                <div key={vehicle.id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover" data-testid={`vehicle-card-${vehicle.id}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={vehicle.photo || CAR_FALLBACKS[idx % CAR_FALLBACKS.length]}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display font-bold text-lg text-slate-950">
                          {vehicle.year} {vehicle.make?.trim()} {vehicle.model?.trim()}
                        </h3>
                        <span className="text-xs font-mono uppercase tracking-wider text-slate-400">{vehicle.plate}</span>
                      </div>
                    </div>
                    <div className="flex items-end gap-8 mt-5">
                      <div>
                        <span className="block font-display text-2xl font-bold text-slate-950">${vehicle.weekly_rate}</span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Per week</span>
                      </div>
                      <div>
                        <span className="block font-display text-2xl font-bold text-slate-950">${vehicle.deposit_amount}</span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Deposit</span>
                      </div>
                    </div>
                    <a href={`${CRM_URL}/book/${vehicle.id}`} className="link-amber mt-5 text-sm" data-testid={`reserve-btn-${vehicle.id}`}>
                      Reserve this car <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 px-4 bg-slate-950 text-white scroll-mt-20" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <span className="eyebrow">How it works</span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-14 max-w-2xl">On the road in three steps.</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/10 rounded-2xl p-7 hover:bg-white/[0.07] transition-colors" data-testid={`step-${i}`}>
                <div className="flex items-center justify-between mb-8">
                  <span className="font-display text-3xl font-bold text-amber-500">{s.n}</span>
                  <s.icon className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="font-display text-xl font-bold mb-3">{s.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-4" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Drivers love it</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-950 mt-3 leading-tight">
              "Best decision I made for my Uber side hustle."
            </h2>
            <Quote className="w-9 h-9 text-amber-400 my-6" />
            <p className="text-slate-500 text-lg leading-relaxed max-w-lg">
              No dealership runaround, no surprise fees. I signed the contract on my phone Sunday night and was driving by Monday morning. The team responds fast and the car was spotless.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold">CW</div>
              <div>
                <p className="font-semibold text-slate-950">Calvin W.</p>
                <p className="text-sm text-slate-500">Rideshare driver · Philadelphia</p>
              </div>
              <div className="flex gap-0.5 ml-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
            </div>
          </div>
          <div className="relative">
            <img src={TESTIMONIAL_IMG} alt="Happy rideshare driver" className="rounded-3xl w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 max-w-[220px]">
              <div className="flex gap-0.5 mb-2">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-xs text-slate-600 leading-snug">"Insurance was already set up — picked the car up in 20 minutes."</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto bg-amber-400 rounded-3xl px-8 py-14 sm:px-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-950">Ready to roll? Book in 2 minutes.</h2>
            <p className="text-slate-800/80 mt-3 font-medium">No long contracts. Cancel anytime. Insurance-ready cars.</p>
          </div>
          <a href={`${CRM_URL}/book`}>
            <button className="btn-dark text-base px-8 py-4 whitespace-nowrap" data-testid="cta-reserve-btn">
              Reserve your car <ArrowRight className="w-5 h-5" />
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
